import useSWR from 'swr';
import { getToken, getUser, type AuthUser } from '@/lib/auth';
import { surfacesApi, type SurfaceSnapshot } from '@/api/surfaces';
import {
  ALL_SURFACE_IDS,
  CUSTOMER_SAFE_SURFACE_IDS,
  homePathFor,
} from '@/lib/surfaceManifest';

// Shared consumer of GET /api/v1/me/surfaces — the authoritative, backend-resolved
// answer to "which shell surfaces may this caller see?" (SENSARRA-VERTICAL-TENANT-SURFACE-1).
//
// Fail-closed by construction:
//   * Until the authoritative snapshot resolves, we fall back to the customer-safe
//     set (least privilege). A server-confirmed Director gets the full set on the
//     fast path so the nav does not flash — but that grants Director *chrome* only;
//     every Director data path stays gated backend-side.
//   * On fetch error we keep the fail-closed fallback — never a wider set.
//
// Both AppShell nav and the RequireSurface route guard read this hook, so nav and
// routes gate off the SAME list and cannot drift.

function isServerConfirmedDirector(user: AuthUser | null): boolean {
  return user?.isDirector === true && user.directorStatusSource === 'auth_me';
}

/** The fail-closed synchronous default, used before /me/surfaces resolves. */
export function fallbackVisibleSurfaces(user: AuthUser | null): Set<string> {
  return new Set(isServerConfirmedDirector(user) ? ALL_SURFACE_IDS : CUSTOMER_SAFE_SURFACE_IDS);
}

export interface UseSurfacesResult {
  /** The surface ids this caller may see (fallback until `resolved`). */
  surfaces: Set<string>;
  role: SurfaceSnapshot['role'] | null;
  /** True once the authoritative backend snapshot has loaded. */
  resolved: boolean;
  /** True once the fetch has settled either way (loaded OR errored). On error the
   *  fail-closed fallback stands — callers can stop waiting without over-granting. */
  settled: boolean;
  has: (surfaceId: string) => boolean;
  /** Role-appropriate home path (never a surface the caller can't see). */
  homePath: string;
}

export function useSurfaces(): UseSurfacesResult {
  const user = getUser();
  const token = getToken();

  const { data, error } = useSWR<SurfaceSnapshot>(
    token ? ['me:surfaces', user?.uid ?? ''] : null,
    () => surfacesApi.getMySurfaces(),
    { revalidateOnFocus: false, shouldRetryOnError: false, dedupingInterval: 30_000 },
  );

  const surfaces = data ? new Set(data.visible_surfaces) : fallbackVisibleSurfaces(user);

  return {
    surfaces,
    role: data?.role ?? null,
    resolved: Boolean(data),
    settled: Boolean(data) || Boolean(error) || !token,
    has: (surfaceId: string) => surfaces.has(surfaceId),
    homePath: homePathFor(surfaces),
  };
}
